// Affiliate / gear recommendations.
// All affiliate URLs are placeholders — fill in with your real partner links later.

const AFF = '#'; // replace with real affiliate URL when you have partnerships

export const recommendationGroups = {
  // Recommended starter equipment for users with no gym access.
  // Bands are listed first and flagged urgent — they're the highest-ROI first purchase.
  // Items with subItems render as expandable rows showing individual product options.
  // Private-label IronFire band product will replace the bands subItems when sourced.
  calisthenics: {
    title: 'Start your home gym',
    blurb: 'You can do a lot with just bodyweight. A few targeted additions open up significantly more variety and faster results.',
    items: [
      {
        id: 'bands',
        name: 'Resistance Bands',
        priceRange: '$10–40',
        why: 'Highest return on investment in home fitness. Adds real load, packs anywhere, unlocks glutes, rows, and pulling work that bodyweight alone can\'t match.',
        url: AFF,
        urgent: true,
        subItems: [
          { id: 'resistance-band-set', name: 'Resistance Band Set (5-loop)', priceRange: '$20–40', why: 'Progressive resistance from ~5 lb to 200+ lb. Covers most accessory work.', url: AFF },
          { id: 'mini-band-set', name: 'Mini Band Set', priceRange: '$10–20', why: 'Glutes, hip abductors, lateral steps, clamshells — no machine replaces these.', url: AFF },
          { id: 'door-anchor', name: 'Door Anchor Attachment', priceRange: '$8–12', why: 'Turns any door into a cable station for rows, pulldowns, and face pulls.', url: AFF },
        ],
      },
      { id: 'pullup-bar', name: 'Doorway Pull-Up Bar', priceRange: '$25–40', why: 'Unlocks pull-ups, hanging leg raises, and grip work.', url: AFF },
      { id: 'power-tower', name: 'Power Tower', priceRange: '$120–200', why: 'Pull-ups, dips, knee raises in one footprint. Replaces a small gym.', url: AFF },
      { id: 'ab-wheel', name: 'Ab Wheel', priceRange: '$15–25', why: 'Best dollar-per-result core tool ever made.', url: AFF },
      {
        id: 'suspension-trainer',
        name: 'Suspension Trainer and Rings',
        priceRange: '$35–200',
        why: 'Hang anywhere — rows, dips, push-ups, and transitions that challenge even advanced athletes.',
        url: AFF,
        subItems: [
          { id: 'gym-rings', name: 'Wood Gymnastic Rings', priceRange: '$35–60', why: 'Versatile, durable, and the gold standard for ring work. Can hang from any pull-up bar or overhead anchor.', url: AFF },
          { id: 'trx', name: 'TRX Suspension Trainer', priceRange: '$100–200', why: 'Guided strap length, comfortable handles, and a door anchor included. Easier starting point for suspension work.', url: AFF },
        ],
      },
    ],
  },

  // Heavy bag recommended gear
  'heavy-bag': {
    title: 'Protect your hands',
    blurb: 'Heavy bag work without proper gear is the fastest path to a sprained wrist or busted knuckle.',
    items: [
      { id: 'hand-wraps', name: '180" Cotton Hand Wraps', priceRange: '$10–15', why: 'Wrist stability + knuckle protection. Wrap before every session.', url: AFF },
      { id: 'gloves', name: 'Training Gloves (14–16 oz)', priceRange: '$60–120', why: 'Heavier = more protection + arm conditioning. Don\'t cheap out.', url: AFF },
      { id: 'jump-rope', name: 'Speed Rope', priceRange: '$15–25', why: 'Warm-up + conditioning between rounds. Standard boxer\'s tool.', url: AFF },
    ],
  },

  // Cycling indoor gear
  'cycling-indoor': {
    title: 'Indoor cycling kit',
    blurb: 'A few small upgrades make stationary rides way more bearable.',
    items: [
      { id: 'padded-shorts', name: 'Padded Cycling Shorts', priceRange: '$40–80', why: 'After 45 min on the saddle, you\'ll understand.', url: AFF },
      { id: 'cycling-shoes', name: 'Clipless Cycling Shoes', priceRange: '$80–150', why: 'Doubles your power transfer vs. flat pedals.', url: AFF },
      { id: 'fan', name: 'High-Velocity Floor Fan', priceRange: '$50–100', why: 'Without one, you\'ll quit halfway through every session.', url: AFF },
    ],
  },

  // Cycling outdoor gear
  'cycling-outdoor': {
    title: 'Outdoor cycling essentials',
    blurb: 'Road or gravel — these pieces keep you safe, comfortable, and trackable.',
    items: [
      { id: 'helmet-road', name: 'MIPS Road Helmet', priceRange: '$80–200', why: 'MIPS layer reduces rotational impact forces. Non-negotiable.', url: AFF },
      { id: 'padded-shorts-out', name: 'Padded Cycling Bibs', priceRange: '$60–140', why: 'Bibs over shorts on long rides — no waistband digging in.', url: AFF },
      { id: 'cycling-glasses', name: 'Photochromic Cycling Glasses', priceRange: '$40–180', why: 'Auto-tinting lenses for sun-into-tree-cover transitions.', url: AFF },
      { id: 'bike-gps', name: 'GPS Bike Computer', priceRange: '$150–500', why: 'Real-time power/HR/route on bars without pulling out your phone.', url: AFF },
      { id: 'flat-kit', name: 'Saddle Flat-Repair Kit', priceRange: '$25–45', why: 'Tube, levers, CO2 inflator. Day-saver on every long ride.', url: AFF },
      { id: 'cycling-gloves', name: 'Padded Cycling Gloves', priceRange: '$25–55', why: 'Cushions ulnar nerve pressure on the bars; saves your hands on long rides.', url: AFF },
    ],
  },

  // Walking / running outdoor gear
  'walk-run': {
    title: 'Walk & run essentials',
    blurb: 'Smart purchases that protect your knees and make miles enjoyable.',
    items: [
      { id: 'running-shoes', name: 'Daily Trainer Running Shoes', priceRange: '$110–180', why: 'Replace every 400–500 miles. Mileage tracker built into Enferno coming soon.', url: AFF },
      { id: 'trail-shoes', name: 'Trail Runners', priceRange: '$120–180', why: 'Lugged outsole for grip, rock plate for protection.', url: AFF },
      { id: 'merino-socks', name: 'Merino Wool Socks', priceRange: '$15–25/pair', why: 'Blister prevention. Worth every dollar on long runs.', url: AFF },
      { id: 'weighted-vest', name: 'Adjustable Weighted Vest (10–20 lb)', priceRange: '$80–140', why: 'Turns walks into strength work. "Rucking" gains.', url: AFF },
    ],
  },

  hiking: {
    title: 'Hiking essentials',
    blurb: 'Day-hike kit that scales up to weekend trips.',
    items: [
      { id: 'hiking-boots', name: 'Mid-Cut Hiking Boots', priceRange: '$140–250', why: 'Ankle support for uneven terrain. Waterproof models for any season.', url: AFF },
      { id: 'daypack', name: '20–30L Hiking Daypack', priceRange: '$60–120', why: 'Hydration sleeve, hip belt, enough room for layers and lunch.', url: AFF },
      { id: 'trekking-poles', name: 'Carbon Trekking Poles', priceRange: '$80–160', why: 'Save your knees on long descents. Boost uphill engagement.', url: AFF },
    ],
  },

  'rock-climbing': {
    title: 'Climbing kit',
    blurb: 'Buy shoes first, then chalk, then everything else as you commit.',
    items: [
      { id: 'climbing-shoes', name: 'All-Around Climbing Shoes', priceRange: '$110–180', why: 'A neutral-to-moderate downturn covers bouldering and routes. Avoid aggressive shoes until you climb 5.11.', url: AFF },
      { id: 'chalk-bag', name: 'Chalk Bag + Refillable Chalk', priceRange: '$25–40', why: 'Loose chalk or ball — pick your preference. Bags last forever.', url: AFF },
      { id: 'harness', name: 'All-Day Climbing Harness', priceRange: '$60–110', why: 'Padded waist + leg loops for hanging belays. Replace every 5 years.', url: AFF },
      { id: 'belay-device', name: 'Assisted-Braking Belay Device', priceRange: '$80–130', why: 'Grigri-style devices catch falls if you slip. Pair with a locking carabiner.', url: AFF },
      { id: 'crash-pad', name: 'Bouldering Crash Pad', priceRange: '$200–350', why: 'Foldable taco or hinge style. Outdoor bouldering is unsafe without one.', url: AFF },
      { id: 'finger-trainer', name: 'Hangboard / Tension Block', priceRange: '$80–200', why: 'The fastest way to build climbing-specific finger strength once you have a base.', url: AFF },
    ],
  },

  swimming: {
    title: 'Pool & open water kit',
    items: [
      { id: 'goggles', name: 'Anti-Fog Swim Goggles', priceRange: '$20–40', why: 'Clear vision under chlorine. Replace every 6 months.', url: AFF },
      { id: 'swim-cap', name: 'Silicone Swim Cap', priceRange: '$10–18', why: 'Reduces drag, protects hair from chlorine.', url: AFF },
      { id: 'pull-buoy', name: 'Pull Buoy + Kickboard Combo', priceRange: '$25–40', why: 'Isolate upper or lower body for technique work.', url: AFF },
    ],
  },

  rowing: {
    title: 'Rowing kit',
    items: [
      { id: 'concept2', name: 'Concept2 Model D Rower', priceRange: '$900–1,100', why: 'The gold standard. Bombproof, accurate, holds value forever.', url: AFF },
      { id: 'hr-monitor', name: 'Chest Heart Rate Monitor', priceRange: '$60–100', why: 'Pairs with the Concept2 PM5 for zone training.', url: AFF },
    ],
  },

  'jump-rope': {
    title: 'Jump rope essentials',
    items: [
      { id: 'speed-rope', name: 'Ball-Bearing Speed Rope', priceRange: '$15–30', why: 'Smooth rotation, adjustable length. Lasts years.', url: AFF },
      { id: 'weighted-rope', name: 'Weighted Jump Rope', priceRange: '$25–50', why: 'Builds shoulder endurance and grip.', url: AFF },
      { id: 'jump-mat', name: 'Cushioned Jump Mat', priceRange: '$30–60', why: 'Saves knees and hardwood floors.', url: AFF },
    ],
  },

  'full-body-cardio': {
    title: 'Bodyweight conditioning kit',
    items: [
      { id: 'kettlebell', name: 'Cast-Iron Kettlebell (35–53 lb)', priceRange: '$60–110', why: 'Swings, snatches, get-ups — one tool, hundreds of workouts.', url: AFF },
      { id: 'cross-shoes', name: 'Cross-Training Shoes', priceRange: '$110–160', why: 'Flat-soled stability for lifts + lateral support for HIIT.', url: AFF },
      { id: 'interval-timer', name: 'Tabata / Interval Timer App', priceRange: 'Free', why: 'A reliable timer is non-negotiable for HIIT work.', url: AFF },
    ],
  },

  // Martial arts gear by discipline
  bjj: {
    title: 'BJJ essentials',
    blurb: 'Quality gi and protection. You spend more time in this gear than you think.',
    items: [
      { id: 'gi', name: 'Pearl Weave Gi', priceRange: '$80–180', why: 'Breathes, durable, IBJJF-legal. Skip the cheap pajamas.', url: AFF },
      { id: 'rashguard', name: 'Long-Sleeve Rashguard', priceRange: '$35–55', why: 'No-gi essential. Reduces mat burn, fits under the gi too.', url: AFF },
      { id: 'mouthguard', name: 'Boil-and-Bite Mouthguard', priceRange: '$15–25', why: 'Cheap insurance for your teeth during rolls.', url: AFF },
      { id: 'gi-bag', name: 'Vented Gear Bag', priceRange: '$40–60', why: 'Wet gi + closed bag = mold city. Get one with mesh panels.', url: AFF },
    ],
  },
  'muay-thai': {
    title: 'Muay Thai kit',
    blurb: 'Striking, kicking, clinch — you need real protection.',
    items: [
      { id: 'shin-guards', name: 'Leather Shin Guards', priceRange: '$80–140', why: 'Non-negotiable for any contact kicking practice.', url: AFF },
      { id: 'gloves-mt', name: 'Muay Thai Gloves (12–16 oz)', priceRange: '$80–150', why: 'Open-cup design for clinch. Different from boxing gloves.', url: AFF },
      { id: 'wraps-mt', name: 'Hand Wraps (180")', priceRange: '$10–15', why: 'Wrist + knuckle support under gloves.', url: AFF },
      { id: 'thai-shorts', name: 'Thai Shorts', priceRange: '$25–40', why: 'Cut for high kicks. Other shorts will fight you.', url: AFF },
    ],
  },
  boxing: {
    title: 'Boxing kit',
    items: [
      { id: 'gloves-box', name: 'Training Gloves (14–16 oz)', priceRange: '$80–150', why: 'Heavier protects hands + builds endurance.', url: AFF },
      { id: 'wraps-box', name: '180" Hand Wraps', priceRange: '$10–15', why: 'Mandatory under every glove session.', url: AFF },
      { id: 'mouthguard-box', name: 'Mouthguard', priceRange: '$15–30', why: 'Even light sparring deserves real protection.', url: AFF },
    ],
  },
  karate: {
    title: 'Karate essentials',
    items: [
      { id: 'gi-karate', name: 'Lightweight Gi', priceRange: '$50–120', why: 'Snap-pop fabric for kata, durable for kumite.', url: AFF },
      { id: 'belt', name: 'Cotton Belt', priceRange: '$10–20', why: 'Sized for your current rank.', url: AFF },
      { id: 'sparring-gear', name: 'Sparring Set (gloves/foot pads)', priceRange: '$60–120', why: 'Required for most kumite classes.', url: AFF },
    ],
  },
  judo: {
    title: 'Judo essentials',
    items: [
      { id: 'judo-gi', name: 'Double-Weave Judo Gi', priceRange: '$80–180', why: 'Built for gripping and being gripped. Heavier than a BJJ gi.', url: AFF },
      { id: 'mouthguard-judo', name: 'Mouthguard', priceRange: '$15–25', why: 'Throws happen fast.', url: AFF },
    ],
  },
  wrestling: {
    title: 'Wrestling gear',
    items: [
      { id: 'wrestling-shoes', name: 'Wrestling Shoes', priceRange: '$60–140', why: 'Flat sole, ankle support, mat-grip. Required.', url: AFF },
      { id: 'singlet', name: 'Singlet', priceRange: '$30–60', why: 'Standard competition uniform.', url: AFF },
      { id: 'headgear', name: 'Headgear', priceRange: '$30–50', why: 'Cauliflower ear prevention.', url: AFF },
    ],
  },
  taekwondo: {
    title: 'Taekwondo essentials',
    items: [
      { id: 'tkd-uniform', name: 'Lightweight Dobok', priceRange: '$40–90', why: 'Designed for high kicks and dynamic movement.', url: AFF },
      { id: 'sparring-tkd', name: 'Sparring Set + Chest Protector', priceRange: '$80–160', why: 'Olympic-style sparring requires full protection.', url: AFF },
    ],
  },
  mma: {
    title: 'MMA training kit',
    items: [
      { id: 'mma-gloves', name: '4 oz MMA Gloves', priceRange: '$30–60', why: 'Open fingers for grappling, padded for striking.', url: AFF },
      { id: 'shin-guards-mma', name: 'Shin Guards', priceRange: '$80–140', why: 'Required for any kicking sparring.', url: AFF },
      { id: 'rashguard-mma', name: 'Rashguard + Compression Shorts', priceRange: '$60–120', why: 'No-gi standard kit.', url: AFF },
      { id: 'mouthguard-mma', name: 'Custom-Fit Mouthguard', priceRange: '$25–60', why: 'Spend a little extra here — you only get one set of teeth.', url: AFF },
    ],
  },
};

export const getRecommendations = (key) => recommendationGroups[key] || null;
